import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Menu, X, Lock, LogOut } from "lucide-react";
import { logout } from "../../store/slices/authSlice";
import { clearMessage } from "../../store/slices/utilitySlice";
import BusinessSidebar from "../Sidebar/BusinessSidebar";

const BusinessLayout = ({ children }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024); // Open by default on desktop
  const profileRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { loading, message, error } = useSelector((state) => state.utility);

  // Handle window resize to manage sidebar state
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true); // Keep open on desktop
      } else {
        setIsSidebarOpen(false); // Close on mobile
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (message) {
      alert(message);
      dispatch(clearMessage());
    }
    if (error) {
      alert(`Error: ${error}`);
      dispatch(clearMessage());
    }
  }, [message, error, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const getUserInitials = () => {
    if (!user?.name) return "B";
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="flex h-screen bg-bg-primary">
      <BusinessSidebar isOpen={isSidebarOpen} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="theme-header flex items-center justify-between px-6 py-4 border-b border-header-border">
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-md bg-card-bg border border-card-border text-text-primary hover:bg-surface-50 transition-colors"
            >
              {isSidebarOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
            <h1 className="text-lg font-semibold text-text-primary"> {user?.name || "Business User"}</h1>
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-surface-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">
                  {getUserInitials()}
                </div>
                {/* <div className="text-left">
                  <p className="text-sm font-medium text-text-primary">
                    {user?.name || "Business User"}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {user?.mobile || user?.email || ""}
                  </p>
                </div> */}
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white border border-gray-200 z-50">
                  <div className="py-1">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.name || "Business User"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user?.mobile || user?.email || ""}
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        navigate("/business/change-password");
                        setIsProfileOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Lock className="h-4 w-4" />
                      <span>Change Password</span>
                    </button>

                    <div className="border-t border-gray-200">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-bg-secondary p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default BusinessLayout;
